
import React, { useState, useEffect } from 'react';
import { useShortContent, ShortContentItem, ShortContentInput } from '@/hooks/useShortContent';
import { Button } from '@/components/ui/button';
import { PlusCircle, Search, Trash2, Edit, Eye, ArrowUpDown, AlertTriangle, Download, FileText, FileJson, Table2 } from 'lucide-react';
import { TravelBytesGenerator } from '@/components/short-content/admin/TravelBytesGenerator';
import { ExportFieldSelector, EXPORT_FIELDS } from '@/components/short-content/admin/ExportFieldSelector';
import { 
  exportTravelByteToPDF, 
  exportTravelBytesToPDF, 
  exportTravelBytesToCSV, 
  exportTravelBytesToJSON 
} from '@/utils/travelBytesExport';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ShortContentForm } from '@/components/short-content/admin/ShortContentForm';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';

export default function ShortContent() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ShortContentItem | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'draft' | 'published' | 'archived' | 'scheduled' | 'all'>('all');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  
  // Export field selection state
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState<'pdf' | 'csv' | 'json'>('pdf');
  const [selectedExportFields, setSelectedExportFields] = useState<Set<string>>(
    new Set(EXPORT_FIELDS.filter(f => f.defaultSelected).map(f => f.key))
  );
  
  const { 
    items, 
    isLoading, 
    fetchShortContent, 
    createShortContent, 
    updateShortContent, 
    deleteShortContent,
    error,
    totalCount
  } = useShortContent({ 
    limit: 50, 
    status: statusFilter === 'all' ? undefined : statusFilter 
  });
  
  useEffect(() => {
    console.log('ShortContent admin page - status filter:', statusFilter);
    console.log('Items loaded:', items.length);
    console.log('Total count:', totalCount);
    console.log('Loading state:', isLoading);
    console.log('Error:', error);
    
    // Log item statuses to help debug
    if (items.length > 0) {
      const statusCounts = items.reduce((acc, item) => {
        acc[item.status] = (acc[item.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      console.log('Status counts:', statusCounts);
    }
  }, [items, isLoading, error, statusFilter, totalCount]);
  
  // Find items that match search query
  const filteredItems = items.filter(item => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      item.title.toLowerCase().includes(query) ||
      (item.content && item.content.toLowerCase().includes(query)) ||
      (item.category && item.category.toLowerCase().includes(query))
    );
  });
  
  const handleCreateSubmit = async (values: ShortContentInput) => {
    try {
      console.log('Creating short content with values:', values);
      
      // Generate slug if not provided
      if (!values.slug && values.title) {
        values.slug = values.title
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
        console.log('Generated slug:', values.slug);
      }
      
      const { data, error } = await createShortContent(values);
      
      if (error) {
        console.error('Error creating content:', error);
        throw error;
      }
      
      console.log('Content created successfully:', data);
      toast.success('Content created successfully');
      
      setIsCreateDialogOpen(false);
      fetchShortContent({
        status: statusFilter === 'all' ? undefined : statusFilter
      });
    } catch (error: any) {
      console.error('Failed to create content:', error);
      toast.error(`Failed to create content: ${error.message}`);
    }
  };
  
  const handleEditSubmit = async (values: ShortContentInput) => {
    if (!selectedItem?.id) return;
    
    try {
      console.log('Updating content with ID:', selectedItem.id);
      console.log('Update values:', values);
      
      const { data, error } = await updateShortContent(selectedItem.id, values);
      
      if (error) {
        console.error('Error updating content:', error);
        throw error;
      }
      
      console.log('Content updated successfully:', data);
      toast.success('Content updated successfully');
      
      setIsEditDialogOpen(false);
      fetchShortContent({
        status: statusFilter === 'all' ? undefined : statusFilter
      });
    } catch (error: any) {
      console.error('Failed to update content:', error);
      toast.error(`Failed to update content: ${error.message}`);
    }
  };
  
  const handleDeleteConfirm = async () => {
    if (!selectedItem?.id) return;
    
    try {
      console.log('Deleting content with ID:', selectedItem.id);
      
      const { error } = await deleteShortContent(selectedItem.id);
      
      if (error) {
        console.error('Error deleting content:', error);
        throw error;
      }
      
      console.log('Content deleted successfully');
      toast.success('Content deleted successfully');
      
      setIsDeleteDialogOpen(false);
      fetchShortContent({
        status: statusFilter === 'all' ? undefined : statusFilter
      });
    } catch (error: any) {
      console.error('Failed to delete content:', error);
      toast.error(`Failed to delete content: ${error.message}`);
    }
  };
  
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'published': return 'success';
      case 'draft': return 'secondary';
      case 'scheduled': return 'warning';
      case 'archived': return 'outline';
      default: return 'default';
    }
  };

  const handleViewContent = (item: ShortContentItem) => {
    // Redirect to the public view of the content
    if (item.slug) {
      window.open(`/travel-bytes/${item.slug}`, '_blank');
    } else {
      toast.error("This content doesn't have a slug and can't be viewed");
    }
  };

  const handleRefreshData = () => {
    console.log('Manually refreshing data with status filter:', statusFilter);
    fetchShortContent({
      status: statusFilter === 'all' ? undefined : statusFilter,
      limit: 50
    });
    toast.info('Refreshing content list...');
  };

  // Export handlers
  const handleExportSinglePDF = (item: ShortContentItem) => {
    try {
      exportTravelByteToPDF(item);
      toast.success('PDF exported successfully');
    } catch (error) {
      toast.error('Failed to export PDF');
      console.error(error);
    }
  };

  // Open export dialog with selected type
  const handleInitiateExport = (type: 'pdf' | 'csv' | 'json') => {
    const itemsToExport = selectedItems.size > 0
      ? filteredItems.filter(item => selectedItems.has(item.id))
      : filteredItems;
    
    if (itemsToExport.length === 0) {
      toast.error('No items to export');
      return;
    }

    setExportType(type);
    setIsExportDialogOpen(true);
  };

  // Perform actual export with selected fields
  const handleConfirmExport = () => {
    const itemsToExport = selectedItems.size > 0
      ? filteredItems.filter(item => selectedItems.has(item.id))
      : filteredItems;

    try {
      if (exportType === 'pdf') {
        exportTravelBytesToPDF(itemsToExport);
        toast.success(`Exported ${itemsToExport.length} items to PDF`);
      } else if (exportType === 'csv') {
        exportTravelBytesToCSV(itemsToExport, selectedExportFields);
        toast.success(`Exported ${itemsToExport.length} items to CSV`);
      } else if (exportType === 'json') {
        exportTravelBytesToJSON(itemsToExport, selectedExportFields);
        toast.success(`Exported ${itemsToExport.length} items to JSON`);
      }
      setIsExportDialogOpen(false);
    } catch (error) {
      toast.error(`Failed to export ${exportType.toUpperCase()}`);
      console.error(error);
    }
  };

  const handleToggleExportField = (fieldKey: string) => {
    const newFields = new Set(selectedExportFields);
    if (newFields.has(fieldKey)) {
      newFields.delete(fieldKey);
    } else {
      newFields.add(fieldKey);
    }
    setSelectedExportFields(newFields);
  };

  const handleSelectAllFields = () => {
    setSelectedExportFields(new Set(EXPORT_FIELDS.map(f => f.key)));
  };

  const handleDeselectAllFields = () => {
    setSelectedExportFields(new Set());
  };

  const handleSelectAll = () => {
    if (selectedItems.size === filteredItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredItems.map(item => item.id)));
    }
  };

  const handleSelectItem = (id: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  return (
    <div className="container py-8">
      {/* AI Generator Section */}
      <TravelBytesGenerator />
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Short Content</h1>
          <p className="text-muted-foreground">
            Manage tips, facts, quotes and other short content pieces
          </p>
        </div>
        <div className="flex gap-2 mt-4 sm:mt-0">
          <Button onClick={() => setIsCreateDialogOpen(true)} variant="default">
            <PlusCircle className="mr-2 h-4 w-4" />
            Create New
          </Button>
        </div>
      </div>
      
      <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0 sm:space-x-4 mb-6">
        <div className="flex w-full sm:w-auto space-x-2">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search content..."
              className="w-full pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <Select 
            value={statusFilter} 
            onValueChange={(value: 'draft' | 'published' | 'archived' | 'scheduled' | 'all') => setStatusFilter(value)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleRefreshData}
            disabled={isLoading}
          >
            <ArrowUpDown className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          
          <Select onValueChange={(value) => {
            if (value === 'pdf-batch') handleInitiateExport('pdf');
            else if (value === 'csv') handleInitiateExport('csv');
            else if (value === 'json') handleInitiateExport('json');
          }}>
            <SelectTrigger className="w-[140px]">
              <Download className="mr-2 h-4 w-4" />
              <SelectValue placeholder="Export" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pdf-batch">
                <div className="flex items-center">
                  <FileText className="mr-2 h-4 w-4" />
                  Export PDF
                </div>
              </SelectItem>
              <SelectItem value="csv">
                <div className="flex items-center">
                  <Table2 className="mr-2 h-4 w-4" />
                  Export CSV
                </div>
              </SelectItem>
              <SelectItem value="json">
                <div className="flex items-center">
                  <FileJson className="mr-2 h-4 w-4" />
                  Export JSON
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {error && (
        <div className="rounded-md bg-destructive/10 p-4 mb-6">
          <div className="flex items-center">
            <AlertTriangle className="h-4 w-4 mr-2 text-destructive" />
            <p className="text-destructive font-medium">Error loading content: {error.message}</p>
          </div>
          <p className="text-sm text-destructive mt-2">
            There may be an issue with the database connection. Try refreshing the page or check your network connection.
          </p>
        </div>
      )}
      
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">
                <input
                  type="checkbox"
                  checked={selectedItems.size === filteredItems.length && filteredItems.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-input"
                />
              </TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Views</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center">
                  {isLoading ? (
                    'Loading content...'
                  ) : (
                    statusFilter !== 'all' ? 
                      `No ${statusFilter} content found.` :
                      'No content found.'
                  )}
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={selectedItems.has(item.id)}
                      onChange={() => handleSelectItem(item.id)}
                      className="rounded border-input"
                    />
                  </TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center space-x-2">
                      {item.is_featured && (
                        <Badge variant="outline" className="mr-1">Featured</Badge>
                      )}
                      <span>{item.title}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {format(new Date(item.created_at), 'MMM d, yyyy')}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {item.content_type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {item.category || '-'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(item.status) as any}>
                      {item.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{item.view_count}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleExportSinglePDF(item)}
                        title="Export to PDF"
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleViewContent(item)}
                        title="View"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedItem(item);
                          setIsEditDialogOpen(true);
                        }}
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedItem(item);
                          setIsDeleteDialogOpen(true);
                        }}
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      
      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Create New Content</DialogTitle>
            <DialogDescription>
              Add a new piece of short content to your site
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[calc(90vh-120px)] pr-4">
            <ShortContentForm 
              onSubmit={handleCreateSubmit} 
              initialData={{ 
                status: 'draft', 
                content_type: 'tip',
                is_featured: false
              }}
            />
          </ScrollArea>
        </DialogContent>
      </Dialog>
      
      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Edit Content</DialogTitle>
            <DialogDescription>
              Make changes to your content
            </DialogDescription>
          </DialogHeader>
          {selectedItem && (
            <ScrollArea className="max-h-[calc(90vh-120px)] pr-4">
              <ShortContentForm 
                initialData={selectedItem} 
                onSubmit={handleEditSubmit} 
              />
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Content</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this content? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {selectedItem && (
            <div className="py-4">
              <h3 className="font-medium">{selectedItem.title}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {selectedItem.content_type} • {format(new Date(selectedItem.created_at), 'MMM d, yyyy')}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Delete Content
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Field Selection Dialog */}
      <ExportFieldSelector
        open={isExportDialogOpen}
        onOpenChange={setIsExportDialogOpen}
        exportType={exportType}
        selectedFields={selectedExportFields}
        onFieldToggle={handleToggleExportField}
        onSelectAll={handleSelectAllFields}
        onDeselectAll={handleDeselectAllFields}
        onConfirm={handleConfirmExport}
        itemCount={
          selectedItems.size > 0
            ? selectedItems.size
            : filteredItems.length
        }
      />
    </div>
  );
}
